"use strict";
import { createSlice } from "@reduxjs/toolkit";
const initialState = {
  selectedLock: null,
  availableGauges: [],
  filteredGauges: [],
  currentVotes: [],
  pendingVotes: [],
  totalAllocatedWeight: 0,
  totalAllocatedPercentage: 0,
  epochInfo: null,
  searchTerm: "",
  sortBy: "reserveUSD",
  sortDirection: "desc",
  loading: {
    gauges: false,
    userVotes: false,
    transaction: false
  },
  error: null
};
export const votingSlice = createSlice({
  name: "voting",
  initialState,
  reducers: {
    // Lock selection
    setSelectedLock: (state, action) => {
      state.selectedLock = action.payload;
      state.pendingVotes = [];
      state.totalAllocatedWeight = 0;
      state.totalAllocatedPercentage = 0;
    },
    // Gauge data
    setAvailableGauges: (state, action) => {
      state.availableGauges = action.payload;
      state.filteredGauges = action.payload;
    },
    setFilteredGauges: (state, action) => {
      state.filteredGauges = action.payload;
    },
    // Current votes
    setCurrentVotes: (state, action) => {
      state.currentVotes = action.payload;
    },
    // Pending votes management
    addPendingVote: (state, action) => {
      const { poolAddress, poolId, weight, pool } = action.payload;
      state.pendingVotes = state.pendingVotes.filter((v) => v.poolAddress !== poolAddress);
      if (weight > 0) {
        state.pendingVotes.push({
          poolAddress,
          poolId,
          weight,
          percentage: 0,
          // Will be calculated below
          pool
        });
      }
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    removePendingVote: (state, action) => {
      const poolAddress = action.payload;
      state.pendingVotes = state.pendingVotes.filter((v) => v.poolAddress !== poolAddress);
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    updatePendingVoteWeight: (state, action) => {
      const { poolAddress, weight } = action.payload;
      const vote = state.pendingVotes.find((v) => v.poolAddress === poolAddress);
      if (vote) {
        if (weight <= 0) {
          state.pendingVotes = state.pendingVotes.filter((v) => v.poolAddress !== poolAddress);
        } else {
          vote.weight = weight;
          vote.percentage = weight;
        }
        votingSlice.caseReducers.recalculatePercentages(state);
      }
    },
    clearPendingVotes: (state) => {
      state.pendingVotes = [];
      state.totalAllocatedWeight = 0;
      state.totalAllocatedPercentage = 0;
    },
    recalculatePercentages: (state) => {
      const totalWeight = state.pendingVotes.reduce((sum, vote) => sum + vote.weight, 0);
      state.totalAllocatedWeight = totalWeight;
      const totalPercentage = state.pendingVotes.reduce((sum, vote) => sum + (vote.percentage || 0), 0);
      state.totalAllocatedPercentage = totalPercentage;
      if (totalWeight > 0) {
      } else {
        state.pendingVotes.forEach((vote) => {
          vote.percentage = 0;
        });
        state.totalAllocatedPercentage = 0;
      }
    },
    // Epoch information
    setEpochInfo: (state, action) => {
      state.epochInfo = action.payload;
    },
    // UI state
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSortBy: (state, action) => {
      if (state.sortBy === action.payload) {
        state.sortDirection = state.sortDirection === "desc" ? "asc" : "desc";
      } else {
        state.sortBy = action.payload;
        state.sortDirection = "desc";
      }
    },
    // Loading states
    setGaugesLoading: (state, action) => {
      state.loading.gauges = action.payload;
    },
    setUserVotesLoading: (state, action) => {
      state.loading.userVotes = action.payload;
    },
    setTransactionLoading: (state, action) => {
      state.loading.transaction = action.payload;
    },
    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Reset all state
    resetVotingState: (state) => {
      Object.assign(state, initialState);
    }
  }
});
export const {
  setSelectedLock,
  setAvailableGauges,
  setFilteredGauges,
  setCurrentVotes,
  addPendingVote,
  removePendingVote,
  updatePendingVoteWeight,
  clearPendingVotes,
  recalculatePercentages,
  setEpochInfo,
  setSearchTerm,
  setSortBy,
  setGaugesLoading,
  setUserVotesLoading,
  setTransactionLoading,
  setError,
  clearError,
  resetVotingState
} = votingSlice.actions;
export default votingSlice.reducer;
export const selectVotingState = (state) => state.voting;
export const selectSelectedLock = (state) => state.voting.selectedLock;
export const selectAvailableGauges = (state) => state.voting.availableGauges;
export const selectFilteredGauges = (state) => state.voting.filteredGauges;
export const selectPendingVotes = (state) => state.voting.pendingVotes;
export const selectCurrentVotes = (state) => state.voting.currentVotes;
export const selectEpochInfo = (state) => state.voting.epochInfo;
export const selectVotingLoading = (state) => state.voting.loading;
export const selectVotingError = (state) => state.voting.error;
export const selectCanVote = (state) => {
  const { selectedLock, epochInfo } = state.voting;
  return !!(selectedLock && epochInfo && !epochInfo.isDistributionPeriod && Date.now() / 1e3 < epochInfo.epochVoteEnd);
};
export const selectHasPendingVotes = (state) => {
  return state.voting.pendingVotes.length > 0;
};
export const selectVotingPowerUtilization = (state) => {
  const { selectedLock, totalAllocatedPercentage } = state.voting;
  if (!selectedLock) return 0;
  return totalAllocatedPercentage;
};
